import { ElementNotFound } from "@senciamatch/frontend/components/ElementNotFound";
import { LoadingData } from "@senciamatch/frontend/components/LoadingData";
import { formatBuildingShift } from "@senciamatch/frontend/libs/getJapaneseDayOfWeek";
import { trpc } from "@senciamatch/frontend/libs/trpc";
import { useSidebarMenuStore } from "@senciamatch/frontend/stores/useSidebarMenuStore";
import { useWorkShiftEditStore } from "@senciamatch/frontend/stores/useWorkShiftEditStore";
import type { ShiftSymbol } from "@senciamatch/shared/models/staffShift";
import { SHIFT_SYMBOLS } from "@senciamatch/shared/models/staffShift";
import { newWorkShiftCell } from "@senciamatch/shared/models/workShiftCell";
import type { WorkShiftEditSession } from "@senciamatch/shared/models/workShiftEditSession";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { SuccessModal } from "./SuccessModal";

export const ModifyWorkShift = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [name, setName] = useState<string>();
  const [isExperienced, setIsExperienced] = useState(true);
  const [staffShiftSymbols, setStaffShiftSymbols] = useState<ShiftSymbol[]>(SHIFT_SYMBOLS);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const hasInitialized = useRef(false);
  // ストアからフックを取得
  const { currentSessionId, sessions, addSession, setCurrentSession, addChange, updateChange, clearSessions } =
    useWorkShiftEditStore();

  // workShiftIdからworkShiftを取得
  const getWorkShifts = trpc.getWorkShifts.useQuery({ ids: id ? [id] : [] }, { enabled: !!id });

  const workShift = getWorkShifts.data?.[0];

  // Force open sidebar when entering this page
  useEffect(() => {
    useSidebarMenuStore.getState().setModifySidebarIsOpen(true);

    // Clean up: Don't close sidebar when leaving
    // Let user manually close it if they want
  }, []);

  // セッションの確認（新規作成はしない）
  useEffect(() => {
    if (workShift?.workShiftGroupId && !hasInitialized.current) {
      hasInitialized.current = true;

      // 異なるworkShiftGroupIdのセッションが存在する場合はリセット
      const differentGroupSessions = sessions.filter((s) => s.workShiftGroupId !== workShift.workShiftGroupId);
      if (differentGroupSessions.length > 0) {
        // 異なるグループのセッションがある場合は全てクリア
        clearSessions();
      }

      // 現在のworkShiftGroupのセッションがあるか確認
      const currentSessionForThisGroup = sessions.find(
        (s) => s.id === currentSessionId && s.workShiftGroupId === workShift.workShiftGroupId,
      );

      if (currentSessionForThisGroup) {
        // 現在選択されているセッションが同じworkShiftGroupのものであれば、そのまま維持
        return;
      }

      // 現在のセッションが異なるworkShiftGroupのものか、セッションがない場合
      const anySessionForThisGroup = sessions.find((s) => s.workShiftGroupId === workShift.workShiftGroupId);

      if (anySessionForThisGroup) {
        // 同じworkShiftGroupのセッションがあれば、それを選択
        setCurrentSession(anySessionForThisGroup.id);
      }
      // セッションがない場合は何もしない（ユーザーが明示的に作成するまで待つ）
    }
  }, [workShift, sessions, currentSessionId, setCurrentSession, clearSessions]);

  // 候補者リストを取得
  const getCandidates = trpc.candidateByBuildingShift.useQuery(
    {
      buildingShiftId: workShift?.buildingShiftId || "",
      isExperienced,
      name: name === "" ? undefined : name,
    },
    { enabled: !!workShift?.buildingShiftId },
  );

  // 直接WorkShiftを更新するAPI
  const updateWorkShifts = trpc.updateWorkShifts.useMutation();

  if (getWorkShifts.isPending || getCandidates.isPending) return <LoadingData />;
  if (!workShift) return <ElementNotFound />;
  if (!getCandidates.data) return <ElementNotFound />;

  const candidates = getCandidates.data;

  const handleAssign = async (candidate: (typeof candidates)[0]) => {
    console.log("handleAssign called", { currentSessionId, workShift, sessions });

    if (!workShift) return;

    // セッションがない場合は作成
    let sessionId = currentSessionId;
    if (!sessionId) {
      const now = new Date();
      const newSession = {
        id: `session-${workShift.workShiftGroupId}-${Date.now()}`,
        workShiftGroupId: workShift.workShiftGroupId,
        changes: [],
        createdAt: now,
        updatedAt: now,
      };
      addSession(newSession);
      sessionId = newSession.id;
    }

    // 1. storeに変更情報を保存 (ローカルに表示用)
    if (sessionId && workShift) {
      const change = {
        workShiftId: workShift.id,
        originalStaffShiftId: workShift.staffShiftId,
        originalStaffName: workShift.staffName || "未割当",
        newStaffShiftId: candidate.id,
        newStaffName: candidate.staffName,
        workShiftInfo: {
          buildingName: workShift.buildingName || "",
          year: workShift.year,
          month: workShift.month,
          day: workShift.day,
          startHour: workShift.startHour,
          startMinute: workShift.startMinute,
          endHour: workShift.endHour,
          endMinute: workShift.endMinute,
        },
      };

      console.log("Adding/Updating change", { change });

      // 既存の変更がある場合は更新、ない場合は追加
      const existingChange = sessions
        .find((s) => s.id === sessionId)
        ?.changes.find((c) => c.workShiftId === workShift.id);
      if (existingChange) {
        console.log("Updating existing change");
        updateChange(sessionId, workShift.id, change);
      } else {
        console.log("Adding new change");
        addChange(sessionId, change);
      }
    }

    // 2. 変更を適用 (直接WorkShiftを更新)
    try {
      await updateWorkShifts.mutateAsync([
        {
          id: workShift.id,
          workShiftGroupId: workShift.workShiftGroupId,
          staffShiftId: candidate.id,
          ...(workShift.buildingShiftId && { buildingShiftId: workShift.buildingShiftId }),
        },
      ]);

      // 2-a/2-b. 変更後のstaffがその日、他の物件に入っているかチェック
      if (candidate.currentWorkShift && candidate.currentWorkShift.buildingShiftId !== workShift.buildingShiftId) {
        // 2-b. 他の物件に入っていた場合、その変更画面に移動
        // currentWorkShiftにはworkShiftIdが含まれているため、それを使用
        if (candidate.currentWorkShift.workShiftId) {
          navigate(`/matchings/${candidate.currentWorkShift.workShiftId}/modify`);
        } else {
          // workShiftIdがない場合は一覧画面に戻る
          navigate(-1);
        }
      } else {
        // 2-a. 他の物件に入っていなかった場合
        setShowSuccessModal(true);
      }
    } catch (error) {
      console.error("シフト変更エラー:", error);
      alert(`シフト変更に失敗しました: ${error instanceof Error ? error.message : "Unknown error"}`);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <div className="flex flex-col h-screen p-8">
      <div className="flex flex-row justify-between mb-6">
        <div className="flex flex-row gap-4 items-center">
          <button type="button" onClick={handleBack}>
            <img src="/icons/double-arrow-left.svg" alt="<<" className="w-10 h-10 mt-1 p-2 fill-black inline-block" />
          </button>
          <h1 className="text-3xl font-bold">
            {formatBuildingShift({
              year: workShift.year,
              month: workShift.month,
              day: workShift.day,
              buildingName: workShift.buildingName || "",
              startHour: workShift.startHour ?? 0,
              startMinute: workShift.startMinute ?? 0,
              endHour: workShift.endHour ?? 0,
              endMinute: workShift.endMinute ?? 0,
            })}
            のシフト変更
          </h1>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
        <h2 className="text-xl font-bold mb-4">現在の登録状況</h2>
        <div className="flex flex-row gap-2 border border-[#EBEBEB]">
          <p className="bg-[#EBEBEB] p-4 w-40">従業員名</p>
          <p className="p-4">{workShift.staffName || "未割当"}</p>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-lg p-6 flex-grow flex flex-col overflow-hidden">
        <h2 className="text-xl font-bold mb-4">変更予定の従業員一覧</h2>

        <div className="mb-6">
          <p className="text-lg font-bold mb-3">従業員名で絞り込む</p>
          <input
            type="text"
            className="p-3 font-bold border border-[#C2C2C2] w-full rounded-md"
            onChange={(e) => setName(e.target.value)}
            placeholder="従業員名を入力"
          />
        </div>

        <div className="mb-6">
          <p className="text-lg font-bold mb-3">フィルタリング</p>
          <div className="flex flex-row gap-8">
            <label className="flex items-center">
              <input
                type="checkbox"
                className="w-4 h-4"
                checked={isExperienced}
                onChange={(e) => setIsExperienced(e.target.checked)}
              />
              <span className="ml-2">過去経験がある従業員</span>
            </label>
            <div className="flex flex-row gap-6">
              {SHIFT_SYMBOLS.map((symbol) => (
                <label key={symbol} className="flex items-center">
                  <input
                    type="checkbox"
                    className="w-4 h-4"
                    checked={staffShiftSymbols.includes(symbol)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setStaffShiftSymbols([...staffShiftSymbols, symbol]);
                      } else {
                        setStaffShiftSymbols(staffShiftSymbols.filter((s) => s !== symbol));
                      }
                    }}
                  />
                  <span className="ml-2">{symbol}</span>
                </label>
              ))}
            </div>
          </div>
        </div>

        <div className="flex flex-row justify-between items-center mb-4">
          <p className="text-lg font-bold">従業員</p>
          <p className="text-sm text-gray-600">
            全日：Ａ、午前：Ｂ、深夜：Ｅ、未定：Ｇ、予約なし：―、勤務不可：×、全日（深夜可）：ＡＥ
          </p>
        </div>

        <div className="flex-1 overflow-y-auto border border-gray-200 rounded">
          <table className="min-w-full">
            <thead className="sticky top-0 z-10">
              <tr>
                <th className="sticky top-0 bg-gray-100 p-2 border-b border-gray-200 text-left">番号</th>
                <th className="sticky top-0 bg-gray-100 p-2 border-b border-gray-200 text-left">従業員</th>
                <th className="sticky top-0 bg-gray-100 p-2 border-b border-gray-200 text-left">提出シフト</th>
                <th className="sticky top-0 bg-gray-100 p-2 border-b border-gray-200 text-left">現在のシフト</th>
                <th className="sticky top-0 bg-gray-100 p-2 border-b border-gray-200 text-left">操作</th>
              </tr>
            </thead>
            <tbody>
              {candidates
                .filter((candidate) => {
                  // 現在のシフトを担当しているスタッフを除外
                  if (workShift.staffId && candidate.staffId === workShift.staffId) {
                    return false;
                  }
                  // シフトシンボルでフィルタリング
                  return staffShiftSymbols.includes(candidate.shiftSymbol);
                })
                .map((candidate) => (
                  <tr key={candidate.staffId} className="hover:bg-gray-50">
                    <td className="p-2 border-b border-gray-200">{candidate.staffId}</td>
                    <td className="p-2 border-b border-gray-200">{candidate.staffName}</td>
                    <td className="p-2 border-b border-gray-200">{candidate.shiftSymbol}</td>
                    <td className="p-2 border-b border-gray-200">
                      {candidate.currentWorkShift &&
                        formatBuildingShift({
                          year: candidate.currentWorkShift.year,
                          month: candidate.currentWorkShift.month,
                          day: candidate.currentWorkShift.day,
                          buildingName: candidate.currentWorkShift.buildingName ?? "",
                          startHour: candidate.currentWorkShift.startHour,
                          startMinute: candidate.currentWorkShift.startMinute,
                          endHour: candidate.currentWorkShift.endHour,
                          endMinute: candidate.currentWorkShift.endMinute,
                        })}
                    </td>
                    <td className="p-2 border-b border-gray-200">
                      <button
                        type="button"
                        className="text-[#00AF31] hover:underline"
                        onClick={() => handleAssign(candidate)}
                      >
                        この従業員に割り当てる
                      </button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>
      <SuccessModal show={showSuccessModal} onClose={() => setShowSuccessModal(false)} />
    </div>
  );
};
